import { ordersGridColumns } from '../../../components/orders-grid/columns';
import { ExportOptions } from '../../../components/modals/export-records-modal/enums/export-options-enum';
import { emptyArray } from 'ui-components/default-props/empty';
import { mapOrdersFiltersToRequestBody } from '../../../services/filtering/api-param-map-service';
import { getOrderStatusFromViewName } from '../../../services/api/order-status-from-view-service';
import { uiColumnToExportColumnMap } from './ui-column-to-export-column-map';
import { GeneralExportHelper } from '../general-export-helper';
import { getIsBatchDetailsPage } from '../../../store/batches/batches.helpers';
import { messages } from '../messages';
import Global from '../../global-selectors';
import { getSelectedViewFromPathName } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';

const DEFAULT_EXPORT_FORMAT = 'CSV';

class OrderExportHelperClass {
  getExportColumnNameFromUiColumnName(uiColumnName) {
    return uiColumnToExportColumnMap[uiColumnName];
  }

  getExportColumnsForLineItems(intl, exportColumnsWithoutLineItems) {
    const list = [];
    const itemQuantity = exportColumnsWithoutLineItems.find((column) => column.columnId === 'ItemQuantity');
    const itemSku = exportColumnsWithoutLineItems.find((column) => column.columnId === 'ItemSku');
    const itemName = exportColumnsWithoutLineItems.find((column) => column.columnId === 'ItemName');
    if (!itemName) {
      list.push({
        columnId: 'ItemName',
        name: intl.formatMessage(messages.recordExportLineItemName),
      });
    }
    if (!itemSku) {
      list.push({
        columnId: 'ItemSku',
        name: intl.formatMessage(messages.recordExportLineItemSku),
      });
    }
    if (!itemQuantity) {
      list.push({
        columnId: 'ItemQuantity',
        name: intl.formatMessage(messages.recordExportLineItemQuantity),
      });
    }
    return list;
  }

  getRequestColumnConfiguration(columnConfiguration, intl) {
    return columnConfiguration.map((column) => {
      const base = ordersGridColumns.find((baseColumn) => baseColumn.id === column.id);
      const messageDescriptor = base.getMessageDescriptor && base.getMessageDescriptor();
      let name = base.getName && base.getName();

      if (intl && messageDescriptor) {
        name = intl.formatMessage(messageDescriptor);
      }

      return {
        columnId: this.getExportColumnNameFromUiColumnName(column.id),
        name,
      };
    });
  }

  // note: this path is only used by the "default" option
  getExportRequestParameters(
    state,
    viewRecordsOrSelectedRecords,
    primaryRecordsOrLineItems,
    searchTerm,
    contextFilters,
    isOnAdvancedSearch,
    intl,
    history,
  ) {
    const stores = Global.selectSellerActiveStores(state);
    const statusView = getSelectedViewFromPathName(history?.location?.pathname, stores);
    const statusViewId = statusView?.id || '';
    const { storeGuid } = statusView || {};
    const activeFilterView = Global.selectOrdersFilteringActiveFilterView(state);
    const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);
    const activeFilterViewId = activeFilterView.filterViewId;

    const columnConfiguration = Global.selectOrdersGridCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
    });
    const exportColumnsWithoutLineItems = this.getRequestColumnConfiguration(columnConfiguration, intl);
    const shouldExportLineItems = primaryRecordsOrLineItems === ExportOptions.EXPORT_PRIMARY_RECORDS_AND_LINE_ITEMS;
    const exportColumns = shouldExportLineItems
      ? [...exportColumnsWithoutLineItems, ...this.getExportColumnsForLineItems(intl, exportColumnsWithoutLineItems)]
      : exportColumnsWithoutLineItems;
    const selectedIds = Global.selectSelectedOrdersIds(state);
    const noSelectedIds = selectedIds === undefined || selectedIds?.length === 0;
    const allRecords = viewRecordsOrSelectedRecords === ExportOptions.EXPORT_ALL_RECORDS_IN_VIEW;

    const { filters, ordersQuickSearchParams } = this.getFiltersAndSearchParams(
      activeFilterView,
      storeGuid,
      statusViewId,
      searchTerm,
      sellerTimeZone,
    );
    const requestFilters = isOnAdvancedSearch || getIsBatchDetailsPage() ? contextFilters : filters;

    const isQuicksearch = !!searchTerm;
    const requestStatusViewId = storeGuid ? statusViewId.substring(0, statusViewId.indexOf(storeGuid) - 1) : statusViewId;
    const status = getOrderStatusFromViewName(requestStatusViewId);
    const exportRequestBody = {
      fileType: DEFAULT_EXPORT_FORMAT,
      exportColumns: exportColumns.filter((col) => col.columnId),
      includeLineItems: shouldExportLineItems,
      query: {
        fulfillmentPlanId: allRecords ? emptyArray : selectedIds || emptyArray,
      },
      filtersV2: requestFilters,
      isAdvancedSearch: isOnAdvancedSearch,
      shouldUseFulfillmentPlanAmountSummary: true,
    };

    if (isQuicksearch && (noSelectedIds || allRecords)) {
      exportRequestBody.query = {
        searchTerm,
        storeGuids: ordersQuickSearchParams.storeGuids,
      };
      exportRequestBody.orderGridStatusCode = ordersQuickSearchParams.orderGridStatus;
    }

    const requestUrl = this.formRequestUrlForExports(status, noSelectedIds, allRecords, isQuicksearch);
    const initiatedByUserId = Global.selectUserCurrentUserId(state);
    return {
      initiatedByUserId,
      requestUrl,
      requestBody: exportRequestBody,
    };
  }

  getFiltersAndSearchParams(activeFilterView, storeGuid, statusViewId, searchTerm, sellerTimeZone) {
    const appliedFilters = activeFilterView.appliedFilters || [];
    const orderGridStatus = this.getOrderGridStatus(statusViewId, storeGuid);
    const ordersQuickSearchParams = searchTerm ? this.getQuickSearchParams(searchTerm, orderGridStatus, storeGuid) : undefined;
    const requestBody = mapOrdersFiltersToRequestBody(appliedFilters, sellerTimeZone);
    const filters = {
      ...requestBody,
      orderGridStatus,
    };
    if (storeGuid && !ordersQuickSearchParams) {
      filters.storeGuid = [storeGuid];
    }
    return {
      filters,
      ordersQuickSearchParams,
    };
  }

  getQuickSearchParams(searchTerm, orderGridStatus, storeGuid) {
    const params = { searchTerm };
    if (orderGridStatus) {
      params.orderGridStatus = orderGridStatus;
    }
    if (storeGuid) {
      params.storeGuids = [storeGuid];
    }
    return params;
  }

  getOrderGridStatus(statusViewId, storeGuid) {
    const requestStatusViewId = storeGuid ? statusViewId.substring(0, statusViewId.indexOf(storeGuid) - 1) : statusViewId;
    const orderGridStatus = getOrderStatusFromViewName(requestStatusViewId);
    return orderGridStatus;
  }

  buildFulfillmentPlanIdCollectionFilter(selectedOrderIds) {
    const fulfillmentPlanIdFilterName = 'FulfillmentPlanId';
    const fulfillmentPlanIdFilter = {
      column: fulfillmentPlanIdFilterName,
      values: selectedOrderIds,
    };
    const filters = {
      collectionFilters: [fulfillmentPlanIdFilter],
    };
    return filters;
  }

  getCustomExportRequestBody({
    state,
    viewRecordsOrSelectedRecords,
    extractId,
    selectedOrderIds,
    searchTerm,
    contextFilters,
    isOnAdvancedSearch,
    history,
  }) {
    const initiatedByUserId = Global.selectUserCurrentUserId(state);
    const exportSelectedRecords = viewRecordsOrSelectedRecords === ExportOptions.EXPORT_SELECTED_RECORDS;
    if (exportSelectedRecords && selectedOrderIds && selectedOrderIds.length) {
      const filters = this.buildFulfillmentPlanIdCollectionFilter(selectedOrderIds);
      const requestBody = {
        filters,
        extractId,
      };
      return {
        initiatedByUserId,
        requestBody,
      };
    }
    const stores = Global.selectSellerActiveStores(state);
    const statusView = getSelectedViewFromPathName(history?.location?.pathname, stores);
    const statusViewId = statusView?.id;
    const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);
    const storeGuid = statusView?.storeGuid;
    const activeFilterView = Global.selectOrdersFilteringActiveFilterView(state);
    const { filters, ordersQuickSearchParams } = this.getFiltersAndSearchParams(
      activeFilterView,
      storeGuid,
      statusViewId,
      searchTerm,
      sellerTimeZone,
    );
    const requestFilters = isOnAdvancedSearch || getIsBatchDetailsPage() ? contextFilters : filters;

    const requestBody = {
      filters: requestFilters,
      extractId,
      ordersQuickSearchParams,
    };
    return {
      requestBody,
      initiatedByUserId,
    };
  }

  formRequestUrlForExports(statusView, noSelectedIds, allRecords, isQuicksearch = false) {
    const request = [];
    const urlRoot = '/fulfillmentPlan/export';
    if (isQuicksearch && (noSelectedIds || allRecords)) {
      return `${urlRoot}/quicksearch`;
    }
    GeneralExportHelper.addStatusToRequest('orderStatus', statusView, request);
    return `${urlRoot}`;
  }
}

export const OrderExportHelper = new OrderExportHelperClass();
